import { collection, doc, query, where, documentId, setDoc } from "firebase/firestore";
import type { ILibraryRepository } from "~/interfaces/ILibraryRepository";
import { Library } from "~/entities/Library";
import { LibraryContent } from "~/entities/LibraryContent";
import { LibraryResponse } from "~/entities/LibraryResponse";
import { UserLibraryInfo, UserLibraryInfoConverter } from "~/entities/UserLibraryInfo";
import { PlanLibraryInfo, PlanLibraryInfoConverter } from "~/entities/PlanLibraryInfo";
import { FIRESTORE_WHERE_IN_LIMIT } from "~/consts/Firebase";

export class LibraryRepository implements ILibraryRepository {
  /**
   * セレクションを取得する
   * @param planId プランID
   * @param libraryId セレクションID
   */
  async getLibrary(libraryId: string, page: number, pageSize: number): Promise<Library> {
    const paramsObj: { [name: string]: string } = {
      page: page.toString(),
      page_size: pageSize.toString(),
    };
    const searchParams = new URLSearchParams(paramsObj);
    return await GetRequest<Library>(`/library/${libraryId}?` + searchParams.toString());
  }

  // セレクション作成、更新時のJSONを作成する
  createLibraryJson(library: Library, libraryContents: LibraryContent[], directories: LibraryContent[]): string {
    return JSON.stringify({
      library_title: library.libraryTitle,
      library_summary: library.librarySummary,
      contents: libraryContents.map(lc => {
        return {
          contents_id: lc.id,
          display_order: lc.displayOrder,
        };
      }),
      directory: directories.map(lc => {
        return {
          title: lc.contentsTitle,
          thumbnail_key: lc.thumbnailKey,
          display_order: lc.displayOrder,
          contents: lc.directoryContents.map(c => {
            return {
              contents_id: c.id,
              display_order: c.displayOrder,
            };
          }),
        };
      }),
    });
  }

  /**
   * セレクションを追加する
   * @param planId プランID
   * @param library セレクション
   */
  async createLibrary(
    planId: string,
    library: Library,
    libraryContents: LibraryContent[],
    directories: LibraryContent[],
  ): Promise<string> {
    return await PostRequest<string>(
      `/library/${planId}`,
      this.createLibraryJson(library, libraryContents, directories),
    );
  }

  /**
   * セレクションを更新する
   * @param planId プランID
   * @param library セレクション
   */
  async updateLibrary(
    planId: string,
    library: Library,
    libraryContents: LibraryContent[],
    directories: LibraryContent[],
  ): Promise<boolean> {
    return await PutRequest<boolean>(
      `/library/${planId}/${library.libraryId}`,
      this.createLibraryJson(library, libraryContents, directories),
    );
  }

  /**
   * セレクションを削除する
   * @param planId プランID
   * @param libraryId セレクションID
   */
  async deleteLibrary(planId: string, libraryId: string): Promise<boolean> {
    return await DeleteRequest<boolean>(`/library/${planId}/${libraryId}`);
  }

  /**
   * セレクションBrowse
   * @param library_id: string
   */
  async browseLibrary(libraryId: string, userId: string): Promise<Library> {
    const libraryInfo = await this.getUserLibraryInfo(userId, libraryId);
    if (!libraryInfo.read) {
      libraryInfo.read = true;
      libraryInfo.libraryId = libraryId;
      libraryInfo.readAt = new Date();
      await this.updateUserLibraryInfo(userId, libraryId, libraryInfo);
    }
  }

  /**
   * ソート順更新
   * @param planId プランID
   * @param libraries[] セレクション
   */
  async updateDisplaySort(planId: string, libraryIds: string[]): Promise<boolean> {
    return await PatchRequest<boolean>(
      `/library/${planId}/sort`,
      JSON.stringify({
        library_ids: libraryIds,
      }),
    );
  }

  /**
   * セレクション一覧を取得する
   * @param planId プランID
   */
  async getLibraries(planId: string): Promise<LibraryResponse> {
    return await GetRequest<LibraryResponse>(`/library/list/${planId}`);
  }

  // ユーザーごとのセレクション情報一覧を取得する
  // /users/{userId}/library
  userLibraryInfoCollection(userId: string) {
    const { $firestore } = useNuxtApp();
    return collection($firestore, "users", userId, "library").withConverter(UserLibraryInfoConverter);
  }

  // ユーザーごとのセレクション情報を取得する
  // /users/{userId}/library/{libraryId}
  userLibraryInfoDoc(userId: string, libraryId: string) {
    const { $firestore } = useNuxtApp();
    return doc($firestore, "users", userId, "library", libraryId).withConverter(UserLibraryInfoConverter);
  }

  // ユーザーごとのセレクション情報一覧を取得する
  // /users/{userId}/library
  async getUserLibraryInfoList(userId: string, libraryIds: string[]): Promise<UserLibraryInfo[]> {
    if (libraryIds.length > FIRESTORE_WHERE_IN_LIMIT) {
      throw new Error(
        "getUserLibraryInfoList: libraryIds must have 30 or less items due to limit of where in statement of firestore query",
      );
    }
    const querySnapshot = await getFirestoreDocs(
      query(this.userLibraryInfoCollection(userId), where(documentId(), "in", libraryIds)),
    );
    if (querySnapshot.empty) {
      return []; // Or however you wish to handle an empty result
    }
    return querySnapshot.docs.map(
      doc =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as UserLibraryInfo,
    );
  }

  // ユーザーごとのセレクション情報を取得する
  // /users/{userId}/library/{libraryId}
  async getUserLibraryInfo(userId: string, libraryId: string): Promise<UserLibraryInfo> {
    const snap = await getFirestoreDoc(this.userLibraryInfoDoc(userId, libraryId));
    if (!snap.exists()) {
      return UserLibraryInfo.empty();
    }
    return snap.data() as UserLibraryInfo;
  }

  // ユーザーごとのセレクション情報を更新する
  // /users/{userId}/library/{libraryId}
  async updateUserLibraryInfo(userId: string, libraryId: string, libraryInfo: UserLibraryInfo): Promise<void> {
    await setDoc(this.userLibraryInfoDoc(userId, libraryId), libraryInfo);
  }

  // プランごとのライブラリリストを取得
  async getPlanLibraryList(planId: string): Promise<PlanLibraryInfo[]> {
    const q = query(this.planLibraryInfoCollection(planId));
    const libraryInfoList = await getFirestoreDocs(q);
    return libraryInfoList.docs.map(doc => doc.data());
  }

  // プランごとのライブラリ情報を取得 /library/{planId}/library
  planLibraryInfoCollection(planId: string) {
    const { $firestore } = useNuxtApp();
    return collection($firestore, "library", planId, "library").withConverter(PlanLibraryInfoConverter);
  }

  /**
   * ライブラリ一覧とユーザー情報を一括取得（バッチ処理対応）
   * @param planId プランID
   * @param userId ユーザーID
   */
  async getLibrariesWithUserInfo(planId: string, userId: string | undefined): Promise<Library[]> {
    // ライブラリの詳細も必要なので、APIからデータを取得する
    const response = await this.getLibraries(planId);

    // userIdがない場合はそのまま返す
    if (!userId) {
      return response.libraries;
    }

    // TODO 暫定的にすべて未読で初期化
    // API レスポンスから is_unread がなくなったら isUnread は undefined でここに来るので、
    // そうなったらこの forEach は削除する
    response.libraries.forEach(library => {
      library.isUnread = true;
    });

    // Mapを使用して検索性能を向上
    const librariesMap = new Map(response.libraries.map(library => [library.libraryId.toString(), library]));

    // firestoreからユーザーごとのライブラリ情報を取得
    // firestoreのwhere in の制限により、FIRESTORE_WHERE_IN_LIMIT件ずつ取得する
    for (let i = 0; i < response.libraries.length; i += FIRESTORE_WHERE_IN_LIMIT) {
      const libraryIdsSlice = response.libraries.slice(i, i + FIRESTORE_WHERE_IN_LIMIT);
      const libraryInfoList = await this.getUserLibraryInfoList(
        userId,
        libraryIdsSlice.map(library => String(library.libraryId)),
      );
      // ユーザーごとのライブラリ情報を付与
      libraryInfoList.forEach(libraryInfo => {
        const library = librariesMap.get(libraryInfo.libraryId?.toString() ?? "");
        if (library) {
          library.isUnread = !libraryInfo.read;
        }
      });
    }

    return Array.from(librariesMap.values());
  }

  /**
   * プランライブラリの未読情報を一括取得（バッチ処理対応）
   * @param planId プランID
   * @param userId ユーザーID
   */
  async getPlanLibraryUnreadInfo(planId: string, userId: string): Promise<string[]> {
    // ライブラリの詳細は必要ない&API負荷を減らすためfirestoreから取得
    const libraryList = await this.getPlanLibraryList(planId);
    const unreadList: string[] = [];
    const libraryIds = libraryList.map(library => library.id);

    // ライブラリのIDリストからライブラリ情報を取得
    // firestoreのwhere in の制限により、FIRESTORE_WHERE_IN_LIMIT件ずつ取得する
    for (let i = 0; i < libraryIds.length; i += FIRESTORE_WHERE_IN_LIMIT) {
      const libraryIdsSlice = libraryIds.slice(i, i + FIRESTORE_WHERE_IN_LIMIT);
      const libraryInfoList = await this.getUserLibraryInfoList(userId, libraryIdsSlice);

      // Mapを使用して検索性能を向上
      const libraryInfoMap = new Map(libraryInfoList.map(info => [info.libraryId?.toString(), info]));

      // 未読: 存在しないものがあれば未読
      for (const libraryId of libraryIdsSlice) {
        const libraryInfo = libraryInfoMap.get(libraryId.toString());
        if (!libraryInfo) {
          unreadList.push(libraryId);
          break;
        }
      }
      if (unreadList.length > 0) {
        break;
      }
    }

    return unreadList;
  }
}
